import React from 'react'

import { Box, Stack, Typography } from '@mui/material'

import { FIELD_HEIGHT, fieldSurfaceSx } from './fieldMetrics'
import { font, space } from 'settings/tokens'

/**
 * Height of a field-shaped trigger — `FIELD_HEIGHT`, the app's ONE field height.
 *
 * ⚠️ It was a hardcoded 44, with the comment "matches the date trigger it stands
 * beside". It did not. MEASURED in the Provenance filter sheet, two fields
 * stacked directly on top of each other: the Owner trigger 44, the Ownership
 * date field 40.
 *
 * That is the same defect this component's own header documents and was written
 * to end — its table lists "height | 44 | 40" as one of four ways two triggers
 * in one sheet had drifted. Fixing it by introducing a SECOND height token just
 * moved the seam: instead of two triggers disagreeing, the trigger token now
 * disagreed with `FIELD_HEIGHT`, which `Input`, `PhoneField`, `SelectDrawer` and
 * `SortField` all consume. A shared constant that is not the shared constant is
 * not a fix.
 *
 * So it derives. The alias is kept because the name is meaningful at call sites,
 * but it can no longer hold a different number.
 */
export const FIELD_TRIGGER_HEIGHT = FIELD_HEIGHT

interface FieldTriggerProps {
  /** Leading glyph — a search icon, a calendar. */
  icon?: React.ReactNode
  /** The resolved value, when there is one. Rendered in primary text. */
  value?: string | null
  /** Shown muted when there is no value. */
  placeholder: string
  /** Trailing content — a clear affordance, a chevron. */
  trailing?: React.ReactNode
  onClick: () => void
}

/**
 * A read-only, field-SHAPED row that opens something (a picker, a sheet).
 *
 * ## Why this exists
 *
 * The filter sheets grew a trigger per field and each one hand-rolled its
 * surface. Measured side by side in the Scans drawer, the date trigger and the
 * location trigger — one above the other, in the same sheet — differed in FOUR
 * ways at once:
 *
 * |  | date | location |
 * |---|---|---|
 * | radius | 6 (clamped) | 12 hardcoded |
 * | height | 44 | 40 |
 * | border | `border.subtle` | `grey[300]` |
 * | placeholder | 16px | 14px |
 *
 * `fieldMetrics.ts` already predicted this failure in writing — "the clamp
 * existed but only `Input` ever called it" — and it happened again the moment a
 * new trigger was written without spreading `fieldSurfaceSx`.
 *
 * So the clamp is no longer something a call site has to remember: it is inside
 * this component. A field-shaped control that opens a picker uses this, and gets
 * the tenant's radius (clamped), the token border, the shared height and one
 * placeholder size by construction. Never re-declare a trigger's surface.
 *
 * Radius specifically: the clamp is geometric, not decorative — radius does not
 * scale with width, so the brand token that flatters a 44px button reads as a
 * lozenge on a 343px field. See FIELD_RADIUS_MAX.
 */
export const FieldTrigger = ({
  icon,
  value,
  placeholder,
  trailing,
  onClick,
}: FieldTriggerProps) => (
  <Box
    role='button'
    tabIndex={0}
    onClick={onClick}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    }}
    sx={theme => ({
      ...fieldSurfaceSx(theme),
      height: FIELD_TRIGGER_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      px: 2,
      gap: `${space.sm}px`,
      cursor: 'pointer',
      color: 'text.secondary',
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.text.primary}`,
        outlineOffset: '-2px',
      },
    })}
  >
    {icon}
    <Typography
      noWrap
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: font.body,
        color: value ? 'text.primary' : 'text.secondary',
      }}
    >
      {value || placeholder}
    </Typography>
    {trailing && (
      <Stack direction='row' alignItems='center' sx={{ flexShrink: 0 }}>
        {trailing}
      </Stack>
    )}
  </Box>
)

export default FieldTrigger
